import { defineStore } from 'pinia'
import { Analysis } from '@/globals/typescript/models/Analysis'
import type {
  IAnalysis,
  IAnalysisTestAnswer,
  IAnalysisTestSingleSelectColorConfig,
  TResultColorId,
} from '@team-milva/lab-models/resources'
import type { ITextObj } from '@team-milva/lab-models/textObj'
import { collection, onSnapshot, query, where } from 'firebase/firestore'
import { db } from '@/firebase/init'
import { managementApi } from '@/globals/typescript/utils/api/managementClient'
import { useAccountStore } from '@/stores/accountStore'
import type { TCreateAnalysisTestPayload } from '@/stores/analysisTestStore'

// Touched AnalysisTest entries sent alongside an Analysis update — see
// updateAnalysisService.ts (lab-cloud) for the matching batched-write schema.
// Each entry sends its full current field set, not a diff.
type TAnalysisTestUpdateEntrySharedFields = {
  id: string
  name: string
  displayName?: string
  isOptional: boolean
}

export type TAnalysisTestUpdateEntry = TAnalysisTestUpdateEntrySharedFields &
  (
    | {
        type: 'prove-based'
        qaTrigger: 'always' | 'proved' | 'not-proved' | null
        provedTextID: string | null
        notProvedTextID: string | null
        colorConfig: {
          enabled: boolean
          provedColorID: TResultColorId | null
          notProvedColorID: TResultColorId | null
        } | null
      }
    | {
        type: 'value-based'
        hasTooLowToMeasure: boolean
        hasTooHighToMeasure: boolean
        minLimit: number
        maxLimit: number
        unitID: string
        colorConfig: {
          enabled: boolean
          conditions: {
            operator: '<' | '<=' | '>=' | '>'
            value: number
            colorID: TResultColorId | null
            textID: string | null
          }[]
        } | null
      }
    | {
        type: 'single-select'
        answers: IAnalysisTestAnswer[]
        // Optional: left out, the stored classification is left alone (MIL-550).
        colorConfig?: IAnalysisTestSingleSelectColorConfig | null
        // Optional for the same reason: left out, the stored QA setting is left alone (MIL-551).
        qaTrigger?: 'always' | 'answers' | null
        qaAnswerIDs?: string[]
      }
    | {
        type: 'multi-select-text'
        reportDescriptionIDs: string[]
        suffixReportDescriptionIDs: string[]
        answers: IAnalysisTestAnswer[]
        answerMode: 'none' | 'optional' | 'required'
      }
    | {
        type: 'multi-select-number'
        reportDescriptionIDs: string[]
        suffixReportDescriptionIDs: string[]
        unitID: string
        answerOverrides: IAnalysisTestAnswer[]
        hasSumValue: boolean
      }
  )

type CreateAnalysisParams = {
  name: string
  minimumTurnaroundTimeID: string | null
  maximumTurnaroundTimeID: string | null
  analysisTestIDs: string[]
  sampleAreaIDs: string[]
  isOnlyOnePerSample: boolean
  equipmentSelectionEnabled: boolean
  labReportTemplateID: string
  autoDescription?: { enabled: boolean; text: Record<string, string> } | null
  newAnalysisTests?: (TCreateAnalysisTestPayload & { id: string })[]
}

type UpdateAnalysisParams = {
  name?: string
  minimumTurnaroundTimeID?: string | null
  maximumTurnaroundTimeID?: string | null
  analysisTestIDs?: string[]
  sampleAreaIDs?: string[]
  activeState?: 'active' | 'inactive'
  isOnlyOnePerSample?: boolean
  equipmentSelectionEnabled?: boolean
  labReportTemplateID?: string
  autoDescription?: { enabled: boolean; text: ITextObj } | null
  analysisTests?: TAnalysisTestUpdateEntry[]
  newAnalysisTests?: (TCreateAnalysisTestPayload & { id: string })[]
}

export const useAnalysisStore = defineStore('analysis', {
  state: () => ({
    allAnalyses: [] as Analysis[],
    unsubscribeAllAnalyses: null as (() => void) | null,
  }),

  getters: {
    allAnalysesLoaded: (state): boolean => !!state.unsubscribeAllAnalyses,

    /**
     * Analyses the lab currently offers. Pricing surfaces use this rather than
     * `allAnalyses`: a deactivated analysis cannot be ordered, so pricing one
     * is dead configuration — and showing it invites creating more of it.
     *
     * `allAnalyses` stays available for anywhere that must resolve a name for a
     * historical reference (an existing order, a log entry), where hiding the
     * record would lose information rather than prevent a mistake.
     */
    activeAnalyses: (state): Analysis[] =>
      state.allAnalyses.filter((x) => x.activeState === 'active'),
  },

  actions: {
    getAnalyses(): Promise<Analysis[]> {
      if (this.allAnalysesLoaded) {
        return Promise.resolve(this.allAnalyses)
      }

      return new Promise((resolve) => {
        let resolved = false

        const accountStore = useAccountStore()

        const q = query(
          collection(db, 'Apps', '-lab-management-', 'Analyses'),
          where('accountID', '==', accountStore.labAccount?.id)
        )

        const unsubscribe = onSnapshot(q, (querySnapshot) => {
          this.allAnalyses = querySnapshot.docs.map(
            (doc) => new Analysis({ id: doc.id, ...doc.data() } as IAnalysis)
          )
          this.unsubscribeAllAnalyses = unsubscribe

          if (!resolved) {
            resolved = true
            resolve(this.allAnalyses)
          }
        })
      })
    },

    async createAnalysis(params: CreateAnalysisParams): Promise<void> {
      const accountStore = useAccountStore()
      const labAccountID = accountStore.labAccount?.id ?? ''

      // TODO: remove cast after deploying and re-running openapi:gen — the
      // autoDescription field isn't in the codegen types yet
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const result = await (managementApi as any)(
        'post',
        'lab-accounts/{labAccountID}/analyses',
        {
          path: { labAccountID },
          body: {
            name: params.name,
            minimumTurnaroundTimeID: params.minimumTurnaroundTimeID,
            maximumTurnaroundTimeID: params.maximumTurnaroundTimeID,
            analysisTestIDs: params.analysisTestIDs,
            sampleAreaIDs: params.sampleAreaIDs,
            isOnlyOnePerSample: params.isOnlyOnePerSample,
            equipmentSelectionEnabled: params.equipmentSelectionEnabled,
            labReportTemplateID: params.labReportTemplateID,
            autoDescription: params.autoDescription,
            newAnalysisTests: params.newAnalysisTests,
          },
        }
      )
      if (!result.success) throw new Error(result.code)
    },

    async updateAnalysis(
      analysisID: string,
      params: UpdateAnalysisParams
    ): Promise<void> {
      const accountStore = useAccountStore()
      const labAccountID = accountStore.labAccount?.id ?? ''

      // TODO: remove cast after deploying and re-running openapi:gen — same
      // reason as createAnalysis's cast above: newAnalysisTests/analysisTests
      // aren't in the codegen types yet
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const result = await (managementApi as any)(
        'patch',
        'lab-accounts/{labAccountID}/analyses/{analysisID}',
        {
          path: { labAccountID, analysisID },
          body: params,
        }
      )
      if (!result.success) throw new Error(result.code)
    },

    async deactivateAnalysis(analysisID: string): Promise<void> {
      await this.updateAnalysis(analysisID, { activeState: 'inactive' })
    },

    async reactivateAnalysis(analysisID: string): Promise<void> {
      await this.updateAnalysis(analysisID, { activeState: 'active' })
    },

    // Hydrates from a Display's public data endpoint — see orderStore.ts.
    // Unlike the onSnapshot mapping, `id` is already merged into each raw
    // element server-side (Analyses documents don't store their own `id`).
    hydrateFromPublicData(raw: unknown[]): void {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.allAnalyses = (raw as any[]).map((x) => new Analysis(x))
    },

    resetStore(): void {
      this.allAnalyses = []

      if (this.unsubscribeAllAnalyses) {
        this.unsubscribeAllAnalyses()
        this.unsubscribeAllAnalyses = null
      }
    },
  },
})

